import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
    Card,
    Grid,
    Divider,
    Chip,
    Stack,
} from "@mui/material";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";

import CrudService from "services/cruds-service";

function CertificateDetails() {
    const [sp] = useSearchParams();
    const id = sp.get("id");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [fileName, setFileName] = useState("");
    const [expireDate, setExpireDate] = useState("");
    const [filePath, setFilePath] = useState(null);
    const [mimeType, setMimeType] = useState(null);

    const [notification, setNotification] = useState({
        value: false,
        color: "info",
        text: "",
    });

    const showNotification = useCallback((text, color = "info") => {
        setNotification({ value: true, color, text });
    }, []);

    useEffect(() => {
        const loadCertificate = async () => {
            try {
                setLoading(true);

                const res = await CrudService.getCertificate(id);
                const a = res.data.attributes;

                setFileName(a.fileName || "");
                setExpireDate(a.validUntil ? a.validUntil.slice(0, 10) : "");
                setFilePath(a.filePath || null);
                setMimeType(a.mimeType || null);
            } catch (err) {
                showNotification("Failed to load certificate details.", "error");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadCertificate();
        }
    }, [id, showNotification]);

    const publicUrl = useMemo(() => {
        if (!filePath) return null;

        const base = process.env.REACT_APP_API_URL?.replace(/\/+$/, "");
        return `${base}/${filePath}`;
    }, [filePath]);

    const previewUrl = publicUrl
        ? `${publicUrl}#toolbar=0&navpanes=0&page=1&zoom=75`
        : "";

    const isPdf = (mimeType || "").includes("pdf");

    const isExpired = expireDate
        ? dayjs(expireDate).isBefore(dayjs(), "day")
        : false;

    const validateForm = () => {
        const d = dayjs(expireDate, "YYYY-MM-DD", true);

        if (!fileName.trim()) {
            showNotification("Please enter file name.", "error");
            return false;
        }

        if (!expireDate) {
            showNotification("Please select expire date.", "error");
            return false;
        }

        if (!d.isValid()) {
            showNotification("Expire date is invalid.", "error");
            return false;
        }

        if (d.isBefore(dayjs(), "day")) {
            showNotification("Expire date cannot be in the past.", "error");
            return false;
        }

        if (d.isAfter(dayjs("2100-12-31"), "day")) {
            showNotification("Expire date is too far in the future.", "error");
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setSaving(true);

            await CrudService.editCertificate(
                id,
                {
                    fileName: fileName.trim(),
                    validUntil: expireDate || null,
                },
                false
            );

            navigate("/certificates");
        } catch (err) {
            showNotification("Failed to update certificate.", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = () => {
        if (!publicUrl) return;

        const link = document.createElement("a");
        link.href = publicUrl;
        link.download = fileName || "certificate";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return null;

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                {notification.value && (
                    <MDAlert color={notification.color} mb={3} dismissible>
                        <MDTypography color="white" variant="body2">
                            {notification.text}
                        </MDTypography>
                    </MDAlert>
                )}

                <Card sx={{ borderRadius: 3 }}>
                    <MDBox p={3}>
                        <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="bold">
                                    Edit Certificate
                                </MDTypography>
                                <MDTypography variant="button" color="text">
                                    Update certificate details and preview the uploaded file.
                                </MDTypography>
                            </MDBox>

                            <MDButton
                                color="secondary"
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate("/certificates")}
                            >
                                Back
                            </MDButton>
                        </MDBox>

                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #eeeeee",
                                        borderRadius: 3,
                                        height: "100%",
                                    }}
                                >
                                    <MDBox p={3}>
                                        <MDBox display="flex" alignItems="center" gap={1.5} mb={3}>
                                            <MDBox
                                                width={48}
                                                height={48}
                                                borderRadius="14px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    background: isPdf
                                                        ? "linear-gradient(135deg, #d32f2f, #ef5350)"
                                                        : "linear-gradient(135deg, #1976d2, #42a5f5)",
                                                }}
                                            >
                                                {isPdf ? (
                                                    <PictureAsPdfIcon sx={{ color: "#fff" }} />
                                                ) : (
                                                    <InsertDriveFileIcon sx={{ color: "#fff" }} />
                                                )}
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography variant="h6" fontWeight="bold">
                                                    Certificate Info
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text">
                                                    {isPdf ? "PDF Document" : "Document File"}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <Stack spacing={2.5}>
                                            <MDInput
                                                fullWidth
                                                label="File Name"
                                                value={fileName}
                                                onChange={(e) => setFileName(e.target.value)}
                                            />

                                            <MDInput
                                                fullWidth
                                                type="date"
                                                label="Expire Date"
                                                value={expireDate}
                                                onChange={(e) => setExpireDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{
                                                    min: dayjs().format("YYYY-MM-DD"),
                                                    max: "2100-12-31",
                                                }}
                                            />

                                            <Divider />

                                            <MDBox
                                                p={2}
                                                borderRadius="14px"
                                                sx={{
                                                    backgroundColor: isExpired ? "#ffebee" : "#f1f8e9",
                                                    border: isExpired
                                                        ? "1px solid #ffcdd2"
                                                        : "1px solid #c8e6c9",
                                                }}
                                            >
                                                <MDBox display="flex" alignItems="center" gap={1.5}>
                                                    <EventAvailableIcon
                                                        sx={{
                                                            color: isExpired ? "#d32f2f" : "#2e7d32",
                                                        }}
                                                    />

                                                    <MDBox>
                                                        <MDTypography variant="button" fontWeight="bold">
                                                            Expire Status
                                                        </MDTypography>

                                                        <MDTypography display="block" variant="caption" color="text">
                                                            {expireDate
                                                                ? isExpired
                                                                    ? "This certificate is expired."
                                                                    : "This certificate is active."
                                                                : "No expire date selected."}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography variant="caption" color="text">
                                                    Mime Type
                                                </MDTypography>

                                                <MDTypography display="block" variant="button" fontWeight="bold">
                                                    {mimeType || "Unknown file type"}
                                                </MDTypography>
                                            </MDBox>

                                            {publicUrl && (
                                                <MDButton
                                                    fullWidth
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={handleDownload}
                                                >
                                                    Download File
                                                </MDButton>
                                            )}

                                            <MDButton
                                                fullWidth
                                                variant="contained"
                                                disabled={saving}
                                                startIcon={<SaveIcon />}
                                                onClick={handleSave}
                                                sx={{
                                                    background:
                                                        "linear-gradient(135deg, #43a047, #66bb6a)",
                                                    color: "#fff",
                                                    boxShadow: "0 8px 18px rgba(67,160,71,0.25)",
                                                    "&:hover": {
                                                        background:
                                                            "linear-gradient(135deg, #388e3c, #43a047)",
                                                    },
                                                }}
                                            >
                                                {saving ? "Saving..." : "Save Changes"}
                                            </MDButton>
                                        </Stack>
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #eeeeee",
                                        borderRadius: 3,
                                        overflow: "hidden",
                                    }}
                                >
                                    <MDBox
                                        px={3}
                                        py={2}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ backgroundColor: "#fafafa" }}
                                    >
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="bold">
                                                File Preview
                                            </MDTypography>

                                            <MDTypography display="block" variant="caption" color="text">
                                                {fileName || "Certificate file"}
                                            </MDTypography>
                                        </MDBox>

                                        <Chip
                                            size="small"
                                            label={isPdf ? "Preview Available" : "Download Required"}
                                            color={isPdf ? "success" : "warning"}
                                            variant="outlined"
                                        />
                                    </MDBox>

                                    <Divider />

                                    {publicUrl && isPdf && (
                                        <MDBox
                                            sx={{
                                                height: 680,
                                                backgroundColor: "#f3f4f6",
                                            }}
                                        >
                                            <iframe
                                                title="Certificate Preview"
                                                src={previewUrl}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    border: "none",
                                                    background: "#fff",
                                                }}
                                            />
                                        </MDBox>
                                    )}

                                    {publicUrl && !isPdf && (
                                        <MDBox
                                            height={420}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            gap={1.5}
                                            sx={{ backgroundColor: "#fafafa" }}
                                        >
                                            <InsertDriveFileIcon
                                                sx={{ fontSize: 64, color: "#9e9e9e" }}
                                            />

                                            <MDTypography variant="h6" fontWeight="bold">
                                                Preview not available
                                            </MDTypography>

                                            <MDTypography variant="button" color="text">
                                                This file type can be viewed after downloading.
                                            </MDTypography>

                                            <MDButton
                                                variant="contained"
                                                startIcon={<DownloadIcon />}
                                                onClick={handleDownload}
                                                sx={{
                                                    mt: 1,
                                                    backgroundColor: "#1976d2",
                                                    color: "#fff",
                                                    "&:hover": { backgroundColor: "#1565c0" },
                                                }}
                                            >
                                                Download
                                            </MDButton>
                                        </MDBox>
                                    )}

                                    {!publicUrl && (
                                        <MDBox
                                            height={420}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            gap={1}
                                        >
                                            <InsertDriveFileIcon
                                                sx={{ fontSize: 64, color: "#bdbdbd" }}
                                            />

                                            <MDTypography variant="button" color="text">
                                                No file uploaded.
                                            </MDTypography>
                                        </MDBox>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
}

export default CertificateDetails;